import { AccountActivityDay } from '@/services/whatsapp-account.service';

type Props = {
  days: AccountActivityDay[];
  height?: number;
};

const short = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

/**
 * Messages-per-day bars for one account. Plots the daily total in a single hue,
 * so no legend is needed; the inbound/outbound split lives in each bar's
 * tooltip. Plain flex divs rather than a chart library — 15 bars with no axes
 * doesn't justify a Recharts mount per card.
 */
export function AccountDayBars({ days, height = 48 }: Props) {
  const totals = days.map((d) => d.inbound + d.outbound);
  const busiest = Math.max(...totals, 0);

  if (days.length === 0 || busiest === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-3 py-4 text-center text-2xs text-text-muted">
        No messages in this period
      </div>
    );
  }

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">
          Messages per day · last {days.length} days
        </span>
        <span className="text-2xs text-text-muted">busiest {busiest}</span>
      </div>

      {/* 2px gap between adjacent bars keeps them readable as separate marks. */}
      <div className="flex items-end gap-[2px]" style={{ height }}>
        {days.map((d) => {
          const total = d.inbound + d.outbound;
          // Floor at 2px so a day with traffic never renders as nothing.
          const h = total === 0 ? 0 : Math.max(2, Math.round((total / busiest) * height));
          return (
            <div
              key={d.date}
              className="group relative flex-1"
              style={{ height }}
              title={`${short(d.date)} · ${total} total (${d.inbound} in, ${d.outbound} out)`}
            >
              <div
                className="absolute bottom-0 w-full rounded-t bg-[var(--color-chart-series)] transition-opacity group-hover:opacity-80"
                style={{ height: h }}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-1 flex justify-between text-[9px] text-text-muted">
        <span>{short(days[0].date)}</span>
        <span>Today</span>
      </div>
    </div>
  );
}
